import React, { useMemo } from 'react'

import { useScript } from 'hoofd'
import { Trans, useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { DocStep } from '../shared/DocStep'

const SignIn = () => {
  const { t } = useLingui()

  const howToData = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Sign Up and Sign In to Guild',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Open the sign-in page',
            text: 'Click the profile icon in the top-right corner of the navigation bar and select Sign In.',
          },
          {
            '@type': 'HowToStep',
            name: 'Enter your email address',
            text: 'Type your email address into the email field. If you do not have an account, one will be created automatically.',
          },
          {
            '@type': 'HowToStep',
            name: 'Check your email for the code',
            text: 'Guild sends a 5-digit verification code to your email. Open the email and note the code.',
          },
          {
            '@type': 'HowToStep',
            name: 'Enter the verification code',
            text: 'Paste or type the 5-digit code into the code input on the sign-in screen. The form submits automatically once all digits are entered.',
          },
        ],
      }),
    []
  )
  useScript({ type: 'application/ld+json', text: howToData })

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild uses passwordless magic link authentication — there are no
            passwords to create or remember. Whether you are signing up for the
            first time or signing back in, the process is the same: enter your
            email, receive a one-time code, and you are in.
          </Trans>
        </Paragraph>
      </Card>

      <Card gap='$4'>
        <DocSection id='how-to-sign-in' title={t`How It Works`}>
          <DocStep
            number={1}
            title={t`Open the sign-in page`}
            description={t`Click the profile icon in the top-right corner of the navigation bar and select Sign In. You will be taken to the sign-in screen.`}
            screenshot={{
              src: 'docs/account/sign-in-page.png',
              alt: 'Sign in page with email address input field and Sign In button',
              caption: t`The sign-in page where you enter your email address`,
            }}
          />
          <DocStep
            number={2}
            title={t`Enter your email address`}
            description={t`Type your email address and click Sign In. If you do not have an account yet, one is created for you automatically — there is no separate sign-up form.`}
          />
          <DocStep
            number={3}
            title={t`Check your email for the code`}
            description={t`Guild sends a 5-digit verification code to your email. The code is included in the email subject line for quick reference. Codes expire after 10 minutes.`}
          />
          <DocStep
            number={4}
            title={t`Enter the verification code`}
            description={t`Paste the full 5-digit code into the first input box — it auto-fills all five boxes. The form submits automatically once all digits are entered, and you are signed in.`}
            screenshot={{
              src: 'docs/account/auth-code-page.png',
              alt: 'Auth code entry page with five individual digit input boxes for the 5-digit verification code',
              caption: t`Enter your 5-digit verification code to complete sign-in`,
            }}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='new-accounts' title={t`New Accounts`}>
          <Paragraph size='$5'>
            <Trans>
              If you enter an email address that is not yet associated with an
              account, Guild creates one for you during the sign-in flow. There is
              no separate sign-up process — just enter your email and verify the
              code. After signing in for the first time, you can customize your
              profile with a display name, photo, and bio.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='google-sign-in' title={t`Sign In with Google`}>
          <Paragraph size='$5'>
            <Trans>
              If you prefer, you can sign in with your Google account. Click the{' '}
              <Paragraph size='$5' fontWeight='$7'>Continue with Google</Paragraph>{' '}
              button on the sign-in page to authenticate through Google. This links
              your Google account to your Guild account, so future sign-ins are a
              single click.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='troubleshooting' title={t`Troubleshooting`}>
          <Paragraph size='$5'>
            <Trans>
              If you do not receive the verification email, check your spam or junk
              folder. The email is sent from Guild and the subject line contains
              your 5-digit code. If the code has expired after 10 minutes, return to
              the sign-in page and request a new one by entering your email again.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default SignIn
